import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { money2, errMsg } from "./payrollHelpers";

export default function AddHolidayPayModal({ employeeId, employeeName, defaultRate, onClose, onSubmit }) {
  const [holiday, setHoliday] = useState("");
  const [hours, setHours] = useState("8");
  const [rate, setRate] = useState(defaultRate || 0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const total = (parseFloat(hours) || 0) * (parseFloat(rate) || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!holiday.trim()) return toast.error("A holiday name is required");
    const hrs = parseFloat(hours);
    if (Number.isNaN(hrs) || hrs <= 0) return toast.error("Enter valid hours");
    setBusy(true);
    try {
      await onSubmit({
        employeeId, category: "HOLIDAY", adjustmentAmount: Math.round(total * 100) / 100,
        hours: hrs, date, reason: holiday.trim(), notes: reason.trim(),
      });
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save holiday pay"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="add-holiday-pay-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Add Holiday Pay - {employeeName}</div>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Holiday <span className="text-rose-500">*</span></label>
            <input value={holiday} onChange={(e) => setHoliday(e.target.value)} data-testid="holiday-name-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" placeholder="e.g. Thanksgiving" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Hours <span className="text-rose-500">*</span></label>
              <input type="number" step="0.25" min="0" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="holiday-hours-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Rate ($/hr)</label>
              <input type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="holiday-rate-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="holiday-date-input" className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-[13px]" />
            </div>
          </div>
          <div className="text-[12px] text-slate-600">Total: <span className="font-semibold text-slate-900" data-testid="holiday-total-display">{money2(total)}</span></div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1">Reason</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="holiday-reason-input" className="w-full px-2.5 py-2 rounded-md border border-slate-200 text-[13px] resize-none" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-200 text-[13px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy} data-testid="holiday-submit-btn" className="h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[13px] font-semibold">{busy ? "Saving..." : "Save Holiday Pay"}</button>
        </div>
      </form>
    </div>
  );
}
